import React, { useCallback, useRef } from 'react';
import styles from './TimelineRuler.module.css';

interface TimelineRulerProps {
  totalBeats: number;
  zoom: number;
  beatWidth: number;
  beatsPerMeasure?: number;
  /** Called when user clicks to seek (GRE-115) */
  onSeek?: (beat: number) => void;
}

export const TimelineRuler: React.FC<TimelineRulerProps> = ({
  totalBeats,
  zoom,
  beatWidth,
  beatsPerMeasure = 4,
  onSeek,
}) => {
  const rulerRef = useRef<HTMLDivElement>(null);
  const measures = Math.ceil(totalBeats / beatsPerMeasure);
  const measureMarkers: React.ReactNode[] = [];

  for (let i = 1; i <= measures; i++) {
    const x = (i - 1) * beatsPerMeasure * beatWidth * zoom;
    measureMarkers.push(
      <div
        key={`measure-${i}`}
        className={styles.measureMarker}
        style={{ left: `${x}px` }}
      >
        {i}
      </div>
    );
  }

  // Handle click-to-seek (GRE-115)
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSeek || !rulerRef.current) return;

    const rect = rulerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const effectiveBeatWidth = beatWidth * zoom;

    // Convert click position to beat
    const beat = clickX / effectiveBeatWidth;
    const clampedBeat = Math.max(0, Math.min(beat, totalBeats));

    onSeek(clampedBeat);
  }, [onSeek, beatWidth, zoom, totalBeats]);

  return (
    <div
      ref={rulerRef}
      className={styles.timelineRuler}
      onClick={handleClick}
      style={{ cursor: onSeek ? 'pointer' : 'default' }}
      role="slider"
      aria-label="Timeline - click to seek"
      aria-valuemin={0}
      aria-valuemax={totalBeats}
    >
      {measureMarkers}
    </div>
  );
};
